import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/lib/supabase'
import { toast } from 'sonner'
import { resolveCustomInputLabels } from '@/lib/utils'
import type { Order, OrderItem, Product, AdminUser } from '@/types/database'

interface AdminStats {
  totalOrders: number
  pendingOrders: number
  totalProducts: number
  featuredProduct: Product | null
  teamMembers: number
  totalRevenue: number
  recentOrders: Order[]
}

export type AdminOrderWithItems = Order & {
  product?: { title: string; slug: string } | null
  orderItems?: OrderItem[]
}

export function useAdminStats() {
  const [stats, setStats] = useState<AdminStats | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  const fetchStats = useCallback(async () => {
    setIsLoading(true)

    const [
      ordersResult,
      pendingResult,
      productsResult,
      featuredResult,
      teamResult,
      revenueResult,
      recentResult,
    ] = await Promise.all([
      supabase.from('orders').select('*', { count: 'exact', head: true }),
      supabase.from('orders').select('*', { count: 'exact', head: true }).eq('fulfillment_status', 'pending'),
      supabase.from('products').select('*', { count: 'exact', head: true }),
      supabase.from('current_featured_product').select('*').maybeSingle(),
      supabase.from('admin_users').select('*', { count: 'exact', head: true }),
      supabase.from('orders').select('total_amount'),
      supabase.from('orders').select('*').order('created_at', { ascending: false }).limit(5),
    ])

    const totalRevenue = revenueResult.data?.reduce((sum, order) => sum + Number(order.total_amount), 0) || 0

    setStats({
      totalOrders: ordersResult.count || 0,
      pendingOrders: pendingResult.count || 0,
      totalProducts: productsResult.count || 0,
      featuredProduct: featuredResult.data as Product | null,
      teamMembers: teamResult.count || 0,
      totalRevenue,
      recentOrders: (recentResult.data as Order[]) || [],
    })

    setIsLoading(false)
  }, [])

  useEffect(() => {
    fetchStats()
  }, [fetchStats])

  // Real-time: update stats on new orders
  useEffect(() => {
    const channel = supabase
      .channel('admin-stats')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'orders' },
        (payload) => {
          const newOrder = payload.new as Order
          setStats(prev => {
            if (!prev) return prev
            return {
              ...prev,
              totalOrders: prev.totalOrders + 1,
              pendingOrders: newOrder.fulfillment_status === 'pending'
                ? prev.pendingOrders + 1
                : prev.pendingOrders,
              totalRevenue: prev.totalRevenue + Number(newOrder.total_amount),
              recentOrders: [newOrder, ...prev.recentOrders.slice(0, 4)],
            }
          })
          toast.success(`New order from ${newOrder.customer_email}!`)
        }
      )
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }, [])

  return { stats, isLoading, refetch: fetchStats }
}

export function useAdminOrders(statusFilter?: string, showArchived?: boolean) {
  const [orders, setOrders] = useState<AdminOrderWithItems[]>([])
  const [isLoading, setIsLoading] = useState(true)

  const fetchOrders = useCallback(async () => {
    setIsLoading(true)

    let query = supabase
      .from('orders')
      .select('*, product:products(title, slug)')
      .order('created_at', { ascending: false })

    // Filter by archive status
    query = query.eq('is_archived', showArchived ? true : false)

    if (statusFilter && statusFilter !== 'all') {
      query = query.eq('fulfillment_status', statusFilter as 'pending' | 'processing' | 'shipped' | 'delivered' | 'cancelled')
    }

    const { data } = await query
    const ordersData = (data as AdminOrderWithItems[]) || []

    // Batch-fetch order_items for all orders
    if (ordersData.length > 0) {
      const orderIds = ordersData.map(o => o.id)
      const { data: allItems } = await supabase
        .from('order_items')
        .select('*')
        .in('order_id', orderIds)
        .order('created_at', { ascending: true })

      if (allItems) {
        const itemsByOrder = new Map<string, OrderItem[]>()
        for (const item of allItems as OrderItem[]) {
          const existing = itemsByOrder.get(item.order_id) || []
          existing.push(item)
          itemsByOrder.set(item.order_id, existing)
        }
        for (const order of ordersData) {
          order.orderItems = itemsByOrder.get(order.id) || []
        }
      }
    }

    setOrders(ordersData)
    setIsLoading(false)
  }, [statusFilter, showArchived])

  useEffect(() => {
    fetchOrders()
  }, [fetchOrders])

  // Real-time: new orders appear instantly
  useEffect(() => {
    const channel = supabase
      .channel('admin-orders')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'orders' },
        () => {
          // Refetch to get full product join + items
          fetchOrders()
        }
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'orders' },
        (payload) => {
          setOrders(prev =>
            prev.map(o => o.id === payload.new.id
              ? { ...o, ...payload.new } as AdminOrderWithItems
              : o
            )
          )
        }
      )
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }, [fetchOrders])

  return { orders, isLoading, refetch: fetchOrders }
}

export function useAdminOrder(orderId: string | undefined) {
  const [order, setOrder] = useState<Order & { product?: Product } | null>(null)
  const [orderItems, setOrderItems] = useState<OrderItem[]>([])
  const [isLoading, setIsLoading] = useState(true)

  const fetchOrder = useCallback(async () => {
    if (!orderId) {
      setIsLoading(false)
      return
    }

    setIsLoading(true)

    const [orderResult, itemsResult] = await Promise.all([
      supabase
        .from('orders')
        .select('*, product:products(*)')
        .eq('id', orderId)
        .maybeSingle(),
      supabase
        .from('order_items')
        .select('*')
        .eq('order_id', orderId)
        .order('created_at', { ascending: true }),
    ])

    setOrder(orderResult.data as (Order & { product?: Product }) | null)

    // Resolve UUID custom_input keys to human-readable labels
    let items = (itemsResult.data as OrderItem[]) || []
    if (items.length > 0) {
      const productIds = [...new Set(items.map(i => i.product_id).filter((id): id is string => !!id))]
      if (productIds.length > 0) {
        const { data: products } = await supabase
          .from('products')
          .select('id, custom_inputs')
          .in('id', productIds)

        if (products) {
          const defsMap = new Map(products.map(p => [p.id, p.custom_inputs]))
          items = items.map(item => ({
            ...item,
            custom_inputs: resolveCustomInputLabels(
              item.custom_inputs as Record<string, string> | null,
              item.product_id ? defsMap.get(item.product_id) : undefined
            ),
          }))
        }
      }
    }

    setOrderItems(items)
    setIsLoading(false)
  }, [orderId])

  useEffect(() => {
    fetchOrder()
  }, [fetchOrder])

  const updateOrder = async (updates: Partial<Order>) => {
    if (!orderId) return { error: 'No order ID' }

    const { error } = await supabase
      .from('orders')
      .update(updates)
      .eq('id', orderId)

    if (!error) {
      await fetchOrder()
    }

    return { error: error?.message || null }
  }

  return { order, orderItems, isLoading, updateOrder, refetch: fetchOrder }
}

export function useAdminTeam() {
  const [members, setMembers] = useState<AdminUser[]>([])
  const [isLoading, setIsLoading] = useState(true)

  const fetchMembers = useCallback(async ({ background = false }: { background?: boolean } = {}) => {
    if (!background) {
      setIsLoading(true)
    }

    try {
      const { data } = await supabase
        .from('admin_users')
        .select('*')
        .order('created_at', { ascending: false })

      setMembers((data as AdminUser[]) || [])
    } finally {
      if (!background) {
        setIsLoading(false)
      }
    }
  }, [])

  useEffect(() => {
    fetchMembers()
  }, [fetchMembers])

  useEffect(() => {
    const channel = supabase
      .channel('admin-team-members')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'admin_users' },
        () => fetchMembers({ background: true })
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'admin_users' },
        () => fetchMembers({ background: true })
      )
      .on(
        'postgres_changes',
        { event: 'DELETE', schema: 'public', table: 'admin_users' },
        () => fetchMembers({ background: true })
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          void fetchMembers({ background: true })
        }

        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
          void fetchMembers({ background: true })
        }
      })

    return () => {
      supabase.removeChannel(channel)
    }
  }, [fetchMembers])

  useEffect(() => {
    const refreshMembers = () => {
      void fetchMembers({ background: true })
    }

    const syncIfVisible = () => {
      if (document.visibilityState === 'visible') {
        refreshMembers()
      }
    }

    const interval = window.setInterval(refreshMembers, 2000)
    window.addEventListener('focus', syncIfVisible)
    document.addEventListener('visibilitychange', syncIfVisible)

    return () => {
      window.clearInterval(interval)
      window.removeEventListener('focus', syncIfVisible)
      document.removeEventListener('visibilitychange', syncIfVisible)
    }
  }, [fetchMembers])

  return { members, isLoading, refetch: fetchMembers }
}

export interface AdminInvitation {
  id: string
  email: string
  role: string
  invited_by: string
  token: string
  expires_at: string
  accepted_at: string | null
  created_at: string
  inviter?: { name: string | null; email: string } | null
}

export function useAdminInvitations() {
  const [invitations, setInvitations] = useState<AdminInvitation[]>([])
  const [isLoading, setIsLoading] = useState(true)

  const fetchInvitations = useCallback(async ({ background = false }: { background?: boolean } = {}) => {
    if (!background) {
      setIsLoading(true)
    }

    try {
      const [{ data: invitationData }, { data: adminUsersData }] = await Promise.all([
        supabase
          .from('admin_invitations')
          .select('*')
          .is('accepted_at', null)
          .order('created_at', { ascending: false }),
        supabase
          .from('admin_users')
          .select('email'),
      ])

      const adminEmailSet = new Set((adminUsersData || []).map((u) => u.email.toLowerCase()))
      const rawInvitations = ((invitationData as unknown as AdminInvitation[]) || [])
        .filter((inv) => !adminEmailSet.has(inv.email.toLowerCase()))

      // Resolve inviter info from admin_users
      const inviterIds = [...new Set(rawInvitations.map(i => i.invited_by).filter(Boolean))]
      if (inviterIds.length > 0) {
        const { data: inviters } = await supabase
          .from('admin_users')
          .select('id, name, email')
          .in('id', inviterIds)

        if (inviters) {
          const inviterMap = new Map(inviters.map(u => [u.id, { name: u.name, email: u.email }]))
          for (const inv of rawInvitations) {
            inv.inviter = inviterMap.get(inv.invited_by) || null
          }
        }
      }

      setInvitations(rawInvitations)
    } finally {
      if (!background) {
        setIsLoading(false)
      }
    }
  }, [])

  useEffect(() => {
    fetchInvitations()
  }, [fetchInvitations])

  useEffect(() => {
    const channel = supabase
      .channel('admin-team-invitations')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'admin_invitations' },
        () => fetchInvitations({ background: true })
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'admin_invitations' },
        () => fetchInvitations({ background: true })
      )
      .on(
        'postgres_changes',
        { event: 'DELETE', schema: 'public', table: 'admin_invitations' },
        () => fetchInvitations({ background: true })
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'admin_users' },
        () => fetchInvitations({ background: true })
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          void fetchInvitations({ background: true })
        }

        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
          void fetchInvitations({ background: true })
        }
      })

    return () => {
      supabase.removeChannel(channel)
    }
  }, [fetchInvitations])

  useEffect(() => {
    const refreshInvitations = () => {
      void fetchInvitations({ background: true })
    }

    const syncIfVisible = () => {
      if (document.visibilityState === 'visible') {
        refreshInvitations()
      }
    }

    const interval = window.setInterval(refreshInvitations, 2000)
    window.addEventListener('focus', syncIfVisible)
    document.addEventListener('visibilitychange', syncIfVisible)

    return () => {
      window.clearInterval(interval)
      window.removeEventListener('focus', syncIfVisible)
      document.removeEventListener('visibilitychange', syncIfVisible)
    }
  }, [fetchInvitations])

  const revokeInvitation = async (id: string) => {
    const { error } = await supabase
      .from('admin_invitations')
      .delete()
      .eq('id', id)

    if (!error) {
      await fetchInvitations()
    }
    return { error: error?.message || null }
  }

  return { invitations, isLoading, refetch: fetchInvitations, revokeInvitation }
}
